import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Tag } from 'primereact/tag';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MagacinProduct, MagacinRemovalReason } from '../types';
import { magacinArrivalsAPI, magacinProductsAPI, magacinRemovalsAPI } from '../services/api';
import './Magacin.css';

const reasonOptions: Array<{ label: string; value: MagacinRemovalReason }> = [
  { label: 'Rashodovanje', value: 'rashodovanje' },
  { label: 'Poklon', value: 'poklon' },
  { label: 'Testiranje', value: 'testiranje' },
  { label: 'Oštećenje', value: 'ostecenje' },
  { label: 'Istek roka', value: 'istek_roka' },
  { label: 'Ostalo', value: 'ostalo' }
];

const fmt = (n: number) => {
  try {
    return n.toLocaleString('sr-RS');
  } catch {
    return String(n);
  }
};

const toDate = (d: any) => (d instanceof Date ? d : d ? new Date(d) : new Date());

const MagacinProducts: React.FC = () => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [products, setProducts] = useState<MagacinProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [globalFilter, setGlobalFilter] = useState('');

  // Add-to-stock dialog
  const [addVisible, setAddVisible] = useState(false);
  const [addProduct, setAddProduct] = useState<MagacinProduct | null>(null);
  const [addForm, setAddForm] = useState({
    quantity: 0,
    arrivalDate: new Date(),
    supplier: '',
    batchNumber: '',
    notes: ''
  });

  // Remove-from-stock dialog
  const [removeVisible, setRemoveVisible] = useState(false);
  const [removeProduct, setRemoveProduct] = useState<MagacinProduct | null>(null);
  const [removeForm, setRemoveForm] = useState({
    quantity: 0,
    removalDate: new Date(),
    reason: 'rashodovanje' as MagacinRemovalReason,
    responsiblePerson: '',
    notes: ''
  });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await magacinProductsAPI.getAll();
      setProducts(data);
    } catch (e: any) {
      console.error(e);
      setError(e?.response?.data?.error || 'Greška pri učitavanju proizvoda');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openAdd = async (row: MagacinProduct) => {
    setAddProduct(row);
    setAddForm({
      quantity: 0,
      arrivalDate: new Date(),
      supplier: '',
      batchNumber: row.batchNumber || '',
      notes: ''
    });
    setAddVisible(true);
  };

  const openRemove = async (row: MagacinProduct) => {
    const responsible = user ? `${user.firstName} ${user.lastName}` : '';
    setRemoveProduct(row);
    setRemoveForm({
      quantity: 0,
      removalDate: new Date(),
      reason: 'rashodovanje',
      responsiblePerson: responsible,
      notes: ''
    });
    setRemoveVisible(true);
  };

  const del = (row: MagacinProduct) => {
    confirmDialog({
      header: 'Potvrda brisanja',
      message: `Obrisati proizvod "${row.name}"?`,
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await magacinProductsAPI.delete(row._id);
          toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Proizvod obrisan', life: 2500 });
          await load();
        } catch (e: any) {
          toast.current?.show({
            severity: 'error',
            summary: 'Greška',
            detail: e?.response?.data?.error || 'Greška pri brisanju',
            life: 5000
          });
        }
      }
    });
  };

  const classBody = (row: MagacinProduct) => (
    <Tag value={`Klasa ${row.productClass}`} severity={row.productClass === 'A' ? 'success' : row.productClass === 'B' ? 'warning' : 'danger'} />
  );

  const weightBody = (field: keyof Pick<MagacinProduct, 'availableWeight' | 'totalWeight' | 'consumedWeight'>) => (row: MagacinProduct) =>
    `${fmt(Number(row[field] || 0))} ${row.unit || ''}`.trim();

  const stockStatus = (row: MagacinProduct) => {
    const available = Number(row.availableWeight || 0);
    const min = Number(row.minStock || 0);
    const max = Number(row.maxStock || 0);
    if (max > 0 && available > max) return { key: 'high' as const, label: 'Visoko' };
    if (available < min) return { key: 'low' as const, label: 'Nisko' };
    return { key: 'ok' as const, label: 'OK' };
  };

  const statusBody = (row: MagacinProduct) => {
    const available = Number(row.availableWeight || 0);
    const max = Number(row.maxStock || 0);
    const pct = max > 0 ? Math.max(0, Math.min(100, (available / max) * 100)) : 0;
    const st = stockStatus(row);

    return (
      <div className="magacin-status">
        <div className="magacin-status-row">
          <span className={`magacin-status-label ${st.key}`}>{st.label}</span>
          <span className="text-700" style={{ fontSize: '0.85rem' }}>
            {fmt(available)} / {fmt(max)} {row.unit}
          </span>
        </div>
        <ProgressBar value={pct} showValue={false} style={{ height: '0.5rem' }} />
        <div className="text-600" style={{ fontSize: '0.8rem' }}>
          {pct.toFixed(1)}% od maksimalnog stanja
        </div>
      </div>
    );
  };

  const actionsBody = (row: MagacinProduct) => (
    <div className="magacin-actions">
      <Button
        className="p-button-sm"
        icon="pi pi-plus"
        rounded
        severity="success"
        tooltip="Dodaj na stanje"
        tooltipOptions={{ position: 'top' }}
        onClick={() => void openAdd(row)}
      />
      <Button
        className="p-button-sm"
        icon="pi pi-minus"
        rounded
        severity="warning"
        tooltip="Skini sa stanja"
        tooltipOptions={{ position: 'top' }}
        onClick={() => void openRemove(row)}
      />
      <Button
        className="p-button-sm"
        icon="pi pi-trash"
        rounded
        severity="danger"
        tooltip="Obriši"
        tooltipOptions={{ position: 'top' }}
        onClick={() => del(row)}
      />
    </div>
  );

  const canAdd = useMemo(() => {
    return addProduct && addForm.quantity > 0 && addForm.supplier.trim() && addForm.batchNumber.trim() && addForm.arrivalDate;
  }, [addProduct, addForm]);

  const canRemove = useMemo(() => {
    return (
      removeProduct &&
      removeForm.quantity > 0 &&
      removeForm.reason &&
      removeForm.responsiblePerson.trim() &&
      removeForm.removalDate
    );
  }, [removeProduct, removeForm]);

  const submitAdd = async () => {
    if (!addProduct) return;
    if (!canAdd) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija', detail: 'Popunite obavezna polja', life: 3000 });
      return;
    }

    setLoading(true);
    try {
      // Requirement: refresh current state before calculation / action
      const fresh = await magacinProductsAPI.getById(addProduct._id);

      await magacinArrivalsAPI.create({
        productId: fresh._id,
        productName: fresh.name,
        productType: fresh.productType,
        quantity: addForm.quantity,
        unit: fresh.unit,
        arrivalDate: toDate(addForm.arrivalDate),
        supplier: addForm.supplier.trim(),
        batchNumber: addForm.batchNumber.trim(),
        notes: addForm.notes || ''
      } as any);

      toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Stanje je povećano', life: 2500 });
      setAddVisible(false);
      await load();
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri dodavanju na stanje',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const submitRemove = async () => {
    if (!removeProduct) return;
    if (!canRemove) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija', detail: 'Popunite obavezna polja', life: 3000 });
      return;
    }

    setLoading(true);
    try {
      // Requirement: refresh current state before calculation / action
      const fresh = await magacinProductsAPI.getById(removeProduct._id);
      if (fresh.availableWeight < removeForm.quantity) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Nedovoljno stanje',
          detail: `Dostupno je ${fmt(fresh.availableWeight)} ${fresh.unit}`,
          life: 4000
        });
        return;
      }

      await magacinRemovalsAPI.create({
        productId: fresh._id,
        productName: fresh.name,
        productType: fresh.productType,
        quantity: removeForm.quantity,
        unit: fresh.unit,
        removalDate: toDate(removeForm.removalDate),
        reason: removeForm.reason,
        responsiblePerson: removeForm.responsiblePerson.trim(),
        notes: removeForm.notes || '',
        department: 'magacin'
      } as any);

      toast.current?.show({ severity: 'success', summary: 'Uspešno', detail: 'Stanje je umanjeno', life: 2500 });
      setRemoveVisible(false);
      await load();
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri skidanju sa stanja',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="magacin-page p-4">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="magacin-header flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="m-0">Rezervni delovi - Magacin</h2>
          <div className="text-600 mt-1">Lista proizvoda</div>
        </div>
        <div className="flex gap-2">
          <Button label="Dodaj novi proizvod" icon="pi pi-plus" severity="info" onClick={() => navigate('/magacin/products/create')} />
          <Button label="Osveži" icon="pi pi-refresh" severity="secondary" onClick={load} />
        </div>
      </div>

      {error && <Message severity="error" text={error} className="mb-3" />}

      <Card>
        <div className="flex justify-content-end mb-3">
          <span className="p-input-icon-left" style={{ width: 'min(380px, 100%)' }}>
            <i className="pi pi-search" />
            <InputText
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Pretraga..."
              style={{ width: '100%' }}
            />
          </span>
        </div>

        <DataTable
          value={products}
          loading={loading}
          paginator
          rows={10}
          emptyMessage="Nema proizvoda"
          className="p-datatable-sm"
          globalFilter={globalFilter}
          globalFilterFields={['name', 'productType', 'category', 'productClass', 'unit']}
        >
          <Column field="name" header="Naziv proizvoda" sortable />
          <Column field="productType" header="Tip" sortable />
          <Column field="category" header="Kategorija" sortable />
          <Column header="Klasa" body={classBody} sortable sortField="productClass" style={{ width: '140px' }} />
          <Column header="Dostupno" body={weightBody('availableWeight')} sortable sortField="availableWeight" style={{ width: '160px' }} />
          <Column header="Ukupno" body={weightBody('totalWeight')} sortable sortField="totalWeight" style={{ width: '160px' }} />
          <Column header="Potrošeno" body={weightBody('consumedWeight')} sortable sortField="consumedWeight" style={{ width: '160px' }} />
          <Column header="Status stanja" body={statusBody} style={{ minWidth: '260px' }} />
          <Column header="Akcije" body={actionsBody} style={{ width: '190px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={`Dodaj na stanje${addProduct ? `: ${addProduct.name}` : ''}`}
        visible={addVisible}
        onHide={() => setAddVisible(false)}
        style={{ width: '700px' }}
        breakpoints={{ '960px': '95vw', '640px': '98vw' }}
        draggable={false}
        modal
        footer={
          <div className="flex justify-content-end gap-2">
            <Button label="Otkaži" className="p-button-secondary p-button-outlined" onClick={() => setAddVisible(false)} />
            <Button label="Dodaj" icon="pi pi-check" severity="info" onClick={submitAdd} loading={loading} disabled={!canAdd} />
          </div>
        }
      >
        <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-6">
            <label>Količina *</label>
            <InputNumber value={addForm.quantity} onValueChange={(e) => setAddForm((p) => ({ ...p, quantity: e.value ?? 0 }))} min={0} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Datum dolaska *</label>
            <Calendar value={addForm.arrivalDate} onChange={(e) => setAddForm((p) => ({ ...p, arrivalDate: toDate(e.value) }))} dateFormat="dd.mm.yy" showIcon />
          </div>
          <div className="field col-12 md:col-6">
            <label>Dobavljač *</label>
            <InputText value={addForm.supplier} onChange={(e) => setAddForm((p) => ({ ...p, supplier: e.target.value }))} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Broj serije *</label>
            <InputText value={addForm.batchNumber} onChange={(e) => setAddForm((p) => ({ ...p, batchNumber: e.target.value }))} />
          </div>
          <div className="field col-12">
            <label>Napomene</label>
            <InputTextarea value={addForm.notes} onChange={(e) => setAddForm((p) => ({ ...p, notes: e.target.value }))} rows={5} />
          </div>
        </div>
      </Dialog>

      <Dialog
        header={`Skini sa stanja${removeProduct ? `: ${removeProduct.name}` : ''}`}
        visible={removeVisible}
        onHide={() => setRemoveVisible(false)}
        style={{ width: '700px' }}
        breakpoints={{ '960px': '95vw', '640px': '98vw' }}
        draggable={false}
        modal
        footer={
          <div className="flex justify-content-end gap-2">
            <Button label="Otkaži" className="p-button-secondary p-button-outlined" onClick={() => setRemoveVisible(false)} />
            <Button label="Skini sa stanja" icon="pi pi-check" severity="warning" onClick={submitRemove} loading={loading} disabled={!canRemove} />
          </div>
        }
      >
        <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-6">
            <label>Količina *</label>
            <InputNumber value={removeForm.quantity} onValueChange={(e) => setRemoveForm((p) => ({ ...p, quantity: e.value ?? 0 }))} min={0} />
          </div>
          <div className="field col-12 md:col-6">
            <label>Datum skidanja *</label>
            <Calendar value={removeForm.removalDate} onChange={(e) => setRemoveForm((p) => ({ ...p, removalDate: toDate(e.value) }))} dateFormat="dd.mm.yy" showIcon />
          </div>
          <div className="field col-12 md:col-6">
            <label>Razlog skidanja *</label>
            <Dropdown
              value={removeForm.reason}
              options={reasonOptions}
              onChange={(e) => setRemoveForm((p) => ({ ...p, reason: e.value }))}
              appendTo="self"
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>Odgovorna osoba *</label>
            <InputText value={removeForm.responsiblePerson} onChange={(e) => setRemoveForm((p) => ({ ...p, responsiblePerson: e.target.value }))} />
          </div>
          <div className="field col-12">
            <label>Napomene</label>
            <InputTextarea value={removeForm.notes} onChange={(e) => setRemoveForm((p) => ({ ...p, notes: e.target.value }))} rows={5} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default MagacinProducts;


